<script lang="ts">
  /**
   * A small pill of capitals on something else: the configuration in a
   * game's top bar, a room's variant in a lobby, a fact on a confirmation
   * card. Never a control. TwinTen's level, lobby, join and versus chips.
   *
   * - `quiet`: the quiet fill with accent words (a top bar's chip).
   * - `fill`: the quiet fill with ink words (a lobby's rule chips).
   * - `strong`: the deep accent with on-accent words (the one chip that matters).
   * - `mark`: the together gradient with on-deep words (something new: "New").
   * - `sunken`: the sunken fill with ink words, a size up (a lobby's summary).
   * - `outline`: a plain outline (a fact).
   * - `accent`: an accent outline with accent words (something running).
   * - `good`: a success outline (a seat that is free).
   *
   * The words are typed in sentence case; the capitals are CSS.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly children: Snippet;
    readonly tone?: 'quiet' | 'fill' | 'strong' | 'mark' | 'sunken' | 'outline' | 'accent' | 'good';
    /** A tooltip: the whole text, when the chip may be cut short. */
    readonly title?: string | undefined;
    readonly testid?: string | undefined;
  }
  const { children, tone = 'quiet', title, testid }: Props = $props();
</script>

<span class="ui-chip ui-chip--{tone}" {title} data-testid={testid}>{@render children()}</span>

<style>
  .ui-chip {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    max-width: 100%;
    padding: 0.2rem 0.55rem;
    border-radius: var(--ui-radius-pill);
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
  }

  .ui-chip--quiet {
    background: var(--ui-surface-2);
    color: var(--ui-accent);
  }

  .ui-chip--fill {
    padding: 0.1rem 0.55rem;
    background: var(--ui-surface-2);
    color: var(--ui-ink);
    letter-spacing: 0.05em;
  }

  .ui-chip--strong {
    padding: 0.1rem 0.55rem;
    background: var(--ui-accent-deep);
    color: var(--ui-on-accent);
    letter-spacing: 0.05em;
  }

  .ui-chip--mark {
    background: linear-gradient(150deg, var(--ui-mark-together-from), var(--ui-mark-together-to));
    color: var(--ui-on-deep);
  }

  .ui-chip--sunken {
    padding: 0.3rem 0.85rem;
    background: var(--ui-sunken);
    /* Its edge, where the sunken step is the page itself (black, 1.4.0). */
    outline: 1px solid var(--ui-line);
    outline-offset: -1px;
    color: var(--ui-ink);
    font-size: 0.78rem;
  }

  .ui-chip--outline,
  .ui-chip--accent,
  .ui-chip--good {
    padding: 0.2rem 0.6rem;
    border: 1px solid var(--ui-muted);
    color: var(--ui-ink);
    letter-spacing: 0.05em;
  }

  .ui-chip--accent {
    border-color: var(--ui-accent);
    color: var(--ui-accent);
  }

  .ui-chip--good {
    border-color: var(--ui-good);
  }
</style>
